/**
 * Shared payload hygiene for Sentry ignored-drop telemetry (BAL-856, BAL-891).
 *
 * The browser, Node and Edge halves all write the SAME `sentry_event_dropped`
 * PostHog event, so they must sanitize identically. Keeping these helpers in
 * one module is what stops the merged table from being redacted on some rows
 * and raw on others.
 *
 * Everything here is defensive by default: a value is redacted unless it is
 * recognisably safe, never kept unless it is recognisably dangerous. The drop
 * path is fed by arbitrary upstream error text, so an allowlist is the only
 * shape that stays correct as new error sources appear.
 */

/**
 * Every reason `beforeSend` can return null, as a stable low-cardinality slug.
 *
 * WHY THE SLUG EXISTS (BAL-953). BAL-856 and BAL-891 wired `reportIgnoredDrop`
 * to the ignore-pattern branches ONLY. Counted on the pre-BAL-953 baseline, the
 * client carried telemetry on 3 of its 12 null-returns (two ignore-pattern
 * branches plus the rrweb guard) and was silent on nine; the server carried it
 * on 2 of 10 and was silent on eight. Sentry's `client_discard /
 * before_send` counter counts ALL of them, so the counter and the telemetry
 * table could never reconcile: a burst on any silent path was invisible BY
 * CONSTRUCTION — the exact failure mode BAL-856 was filed to remove. Measured
 * over the first 24h after the telemetry shipped: 10 Sentry discards, 1 PostHog
 * row.
 *
 * A `pattern` field cannot carry this. Nine of the branches match on a flag, a
 * class name, an env var or a stack frame — there is no regex to name them by.
 *
 * KEEP THESE VALUES STABLE. They are grouping keys in PostHog; renaming one
 * splits its history in two.
 */
export const DROP_REASONS = {
  /** Matched an `IGNORED_ERROR_PATTERNS` entry — `pattern` names which. */
  ignorePattern: 'ignore-pattern',
  /** Vercel preview deployment, dropped wholesale (BAL-791). */
  previewEnvironment: 'preview-environment',
  /** Served from localhost / 127.0.0.1 while tagged as another environment. */
  localhost: 'localhost',
  /** Missing `NEXT_PUBLIC_SUPABASE_*` ZodError, non-production (BAL-169). */
  missingSupabaseKeys: 'missing-supabase-keys',
  /** The getter TypeError from getSupabaseClientKeys's pre-9d29f582f4 catch block (BAL-171). */
  supabaseKeysTypeError: 'supabase-keys-type-error',
  /** Error carries `isExpectedValidation`. */
  expectedValidation: 'expected-validation',
  /** Error carries `alreadyCapturedToSentry` — captured at the boundary. */
  alreadyCaptured: 'already-captured',
  /** `error.name === 'ValidationError'`. */
  validationErrorName: 'validation-error-name',
  /** `error.name === 'AuthWeakPasswordError'` (BAL-576). */
  weakPasswordErrorName: 'weak-password-error-name',
  /** Stack frame from a browser extension or injected third-party script. */
  externalScript: 'external-script',
  /** Next.js `notFound()` control-flow digest (BALLEE-GF / BALLEE-GG). */
  nextNotFound: 'next-not-found',
  /** Error carries `isExpectedAuthMiss` — anonymous caller (BALLEE-D7). */
  expectedAuthMiss: 'expected-auth-miss',
  /** rrweb CSS serializer warning from the replay integration (BALLEE-HG). */
  rrwebCssWarning: 'rrweb-css-warning',
  /**
   * Injected page harness that wraps `addEventListener` and throws inside its
   * own hook (BALLEE-HQ / BALLEE-HR / BALLEE-HS).
   *
   * One throw, three fingerprints: the same failure arrives as a console
   * MESSAGE capture, an `onerror` exception and an `onunhandledrejection`
   * exception. Grouping them under one reason is what makes the class
   * countable as one thing.
   */
  pageHarnessAddEventListenerHook: 'page-harness-addeventlistener-hook',
  /**
   * Next.js RSC prefetch fallback, surfaced as a console capture (BAL-1006).
   *
   * The router logs this when a prefetch falls back to a full navigation. It
   * is normal routing behaviour, not an application fault, so it is dropped —
   * but counted, because a silent drop makes a real prefetch regression
   * indistinguishable from healthy fallback traffic.
   */
  rscPrefetchFallback: 'rsc-prefetch-fallback',
} as const;

export type DropReason = (typeof DROP_REASONS)[keyof typeof DROP_REASONS];

/**
 * Telemetry payload for an event `beforeSend` discarded.
 *
 * `beforeSend` returns null on a drop, so without this the only surviving
 * evidence is Sentry's aggregate `client_discard` counter — which cannot be
 * grouped by reason, pattern, route, or environment.
 */
export interface IgnoredDropInfo {
  /**
   * WHY the event was dropped — see `DROP_REASONS`.
   *
   * This is the field to group by. `pattern` is populated on only some
   * reasons, and never identifies the class on its own — see its docblock
   * below for which, and group by this field instead.
   */
  reason: DropReason;
  /**
   * `RegExp.source` of the matched `IGNORED_ERROR_PATTERNS` entry.
   *
   * Also populated on the branches that match a regex of their OWN rather than
   * an `IGNORED_ERROR_PATTERNS` entry — `rrweb-css-warning` and
   * `rsc-prefetch-fallback` — and on `page-harness-addeventlistener-hook`,
   * which carries a literal slug because it keys on several signals at once
   * and no single regex names it.
   *
   * Empty string on every other reason — those branches match on a flag, a
   * class name or a stack frame, so no pattern exists to name.
   *
   * KEEP THIS LIST IN STEP with the `dropEvent` call sites that pass
   * `pattern`. It is the only statement of what the field means per reason,
   * and a reader who groups by it needs to know which rows are populated.
   */
  pattern: string;
  /**
   * First 120 chars of the error message, PII-redacted.
   *
   * `<suppressed>` when the drop's ORIGIN makes the text unsafe to forward at
   * all, whatever the reason. Decided in `reportIgnoredDrop` from the host and
   * the build, NOT by any one branch — see `SUPPRESSED_MESSAGE`.
   */
  messagePrefix: string;
  /** Request route — normalized, no query string, no opaque segments. */
  route: string;
  /**
   * `production` | `preview` | `development` | … — see `resolveEnvironment`.
   *
   * Sentry's own discard counter (`stats_v2`) cannot be filtered or grouped by
   * environment: `&environment=` is silently ignored and `&groupBy=environment`
   * is rejected outright. So a drop is unattributable to prod vs preview by any
   * route unless the payload carries the field itself (BAL-891).
   */
  environment: string;
}

export type IgnoredDropHandler = (drop: IgnoredDropInfo) => void;

/** Max chars of error message forwarded — enough to identify the class. */
export const MESSAGE_PREFIX_LENGTH = 120;

/**
 * Hard bound on how much of a message the redaction pass will scan.
 *
 * Redaction has to run BEFORE truncation, or a secret could be cut in half
 * and its tail shipped unredacted. But `Error.message` is unbounded — Sentry's
 * `maxValueLength` only rewrites `event.exception.values[].value`, not the raw
 * thrown error — so "redact everything, then keep 120" means an arbitrarily
 * long synchronous scan inside `beforeSend`, on the request path.
 *
 * Scanning the first 1 KB is safe: only `MESSAGE_PREFIX_LENGTH` chars are ever
 * forwarded, so anything a truncation at 1 KB could split is already ~900
 * chars past the end of what ships.
 */
const MAX_REDACTION_SCAN_LENGTH = 1_000;

/** Max chars of route forwarded, after normalization. */
const MAX_ROUTE_LENGTH = 120;

/**
 * Values that must never leave the process inside a telemetry payload.
 *
 * The ignore-list only matches benign classes, but patterns like
 * `/network error/i` and `/rate limit/i` match as a SUBSTRING anywhere in a
 * message, so arbitrary upstream error text reaches `messagePrefix` — a
 * Postgres error carrying a row value (`Key (email)=(dancer@example.com)`), a
 * vendor error echoing a request, and so on.
 */
const PII_REDACTIONS: Array<[RegExp, string]> = [
  // The local part is bounded at RFC 5321's 64-char maximum. Unbounded
  // (`[\w.+-]+@`) this is quadratic: on a long unbroken run of word/dot/plus/
  // hyphen characters with no `@`, the engine retries every suffix from every
  // start position. Measured through this module: 4 KB → 26 ms, 32 KB →
  // 3.9 s, synchronously inside `beforeSend`. Base64url blobs, hex digests
  // and encoded bodies are all exactly that shape.
  [/[\w.+-]{1,64}@[\w-]+\.[\w.-]+/g, '<email>'],
  [
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    '<uuid>',
  ],
  // JWTs. Case-insensitive defensively: no call site lowercases its input any
  // more (BAL-953 fixed the last one, on the browser's exception-value branch),
  // but a case-sensitive rule fails SILENTLY if one reappears — it just stops
  // matching, and a drop leaves no trace to notice it by. The `AKIA` rule below
  // is the one that genuinely cannot be case-folded, since lowercase hex would
  // match half the strings in a stack trace.
  [/\beyJ[\w-]{8,}\.[\w-]+\.[\w-]+/gi, '<jwt>'],
  // Vendor keys, underscore-delimited: Supabase, Stripe, Resend, PostHog,
  // GitHub, webhook secrets.
  [
    /\b(?:sb_secret|sb_publishable|sk_live|sk_test|pk_live|whsec|re|phc|phx|gh[pousr])_[A-Za-z0-9_-]{6,}/gi,
    '<secret>',
  ],
  // Vendor keys, HYPHEN-delimited. A separate rule because the underscore
  // pattern above cannot reach them, and these are the ones that show up on
  // the `/rate limit/i` drop path — AI-provider 429s are exactly that class.
  [/\b(?:sk-ant|sk-proj|sk|xox[baprs])-[A-Za-z0-9_-]{8,}/gi, '<secret>'],
  // AWS access key ids.
  [/\b(?:AKIA|ASIA)[A-Z0-9]{12,}/g, '<secret>'],
  [/\bBearer\s+[A-Za-z0-9._-]{8,}/gi, 'Bearer <token>'],
  // Phone numbers written with separators — `\d{7,}` below cannot see them.
  [/\+\d[\d\s().-]{7,}\d/g, '<phone>'],
  // The same number percent-encoded, as it appears inside a failed request URL
  // echoed into an error message (`?phone=eq.%2B41763669239`). The rule above
  // cannot reach it: `%2B` puts a letter immediately before the digits, so
  // there is no `+` to anchor on, and `\b\d{7,}\b` below has no word boundary
  // after the `B` either. Found by the BAL-953 security review.
  [/%2B\d{7,}/gi, '<phone>'],
  // Long hex runs — session ids, HMACs, digests.
  [/\b[0-9a-f]{16,}\b/gi, '<hex>'],
  // IBANs. Two country letters then a checksum defeats `\b\d{7,}\b` below, and
  // the human-written spaced form (`CH93 0076 2011 …`) defeats it twice over.
  // Reimbursement and payout errors carry these verbatim.
  //
  // AFTER the hex rule on purpose: an uppercase digest whose first four chars
  // happen to read letter-letter-digit-digit would otherwise be labelled
  // `<iban>` instead of `<hex>`. Both redact, so this is a labelling fix, not a
  // leak. Real IBANs are unaffected — a country code is not hex.
  //
  // TWO rules rather than one with an optional separator. Measured during the
  // BAL-953 review: the single form `(?:[ ]?[A-Z0-9]{2,4}){2,8}` is ~1000x
  // slower on a long uppercase run that fails the trailing `\b`, because the
  // engine retries every partition — but it is still LINEAR, since `{2,8}`
  // bounds the search. The catastrophic shape is the UNBOUNDED
  // `(?:[ ]?[A-Z0-9]{2,4})+`, which did not finish on an 80-character run.
  // Below, the spaced form consumes exactly five characters per step and the
  // compact form has a single quantifier, so both are flat.
  //
  // The compact rule's `{11,30}` floor makes 15 the shortest match, which is
  // exactly Norway's IBAN — the shortest that exists. A `{10,30}` floor also
  // claimed 14-character ids of the same shape (`AV1234567890AB`), so this is
  // the tightest bound that still covers every real IBAN.
  //
  // Known over-match, accepted: any 15-to-34-character alphanumeric run shaped
  // letter-letter-digit-digit is claimed, so an opaque id of that shape
  // (`av1234567890abcd`) reads as `<iban>`. Tightening further would drop real
  // IBANs, and the failure direction here is losing a little debugging
  // fidelity, never leaking. Four-letter all-caps groups
  // (`CF12 ABCD EFGH IJKL`) hit the spaced rule the same way.
  // Case-insensitive: a user types their IBAN in lowercase and a validation
  // error echoes it back verbatim. Only safe BECAUSE the hex rule runs first —
  // case-folded, these would otherwise claim lowercase digests before `<hex>`
  // could. Found by the BAL-953 security review.
  [/\b[A-Z]{2}\d{2}(?: [A-Z0-9]{4}){2,7}(?: [A-Z0-9]{1,4})?\b/gi, '<iban>'],
  [/\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/gi, '<iban>'],
  [/\b\d{7,}\b/g, '<num>'],
];

/**
 * Hosts that identify a developer machine rather than a deployment.
 *
 * Loopback in every spelling, the unspecified address, RFC 1918 private
 * ranges, link-local, and the `.local` / `.localhost` suffixes. The private
 * ranges matter: `pnpm dev --hostname 0.0.0.0` and testing from a phone on the
 * LAN both produce a non-loopback host that is still someone's laptop.
 */
const LOCAL_HOSTNAME_PATTERNS = [
  /^localhost$/i,
  /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/,
  /^0\.0\.0\.0$/,
  /^::1?$/,
  /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/,
  /^172\.(?:1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/,
  /^192\.168\.\d{1,3}\.\d{1,3}$/,
  /^169\.254\.\d{1,3}\.\d{1,3}$/,
  // Anchored to a HOST shape, not a bare suffix: an unanchored `/\.local$/`
  // also matches `evil.com/.local`. Unreachable from today's two call sites,
  // which both pass a parsed hostname — but this is a security predicate, and
  // the next caller might pass a raw `Host` header.
  /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.local$/i,
  /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.localhost$/i,
];

/** True when `hostname` names a developer machine. Brackets on IPv6 are ok. */
export function isLocalHostname(hostname: string | undefined): boolean {
  if (!hostname) return false;
  const bare = hostname.replace(/^\[|\]$/g, '');
  return LOCAL_HOSTNAME_PATTERNS.some((pattern) => pattern.test(bare));
}

/**
 * True when the drop came from somewhere that must not forward error text.
 *
 * Two signals, because the host alone leaves holes that are invisible in
 * testing: a tunnel (ngrok and friends) presents a PUBLIC hostname while
 * serving a laptop, a server event can carry no request URL at all (a cron, a
 * startup path), and a relative URL does not parse. All three read as
 * production by host.
 *
 * `NODE_ENV === 'development'` closes them. Next.js sets exactly that under
 * `next dev`, which is how a developer machine runs, and it is the premise the
 * localhost drop filter already rests on. Deliberately NOT `VERCEL_ENV`, which
 * this very module documents as unreliable — a misconfigured local environment
 * stamps `production` on it, which is the whole reason the localhost filter
 * exists. Deliberately not `process.env.VERCEL` either: that couples telemetry
 * hygiene to one hosting provider.
 *
 * Testing for `development` rather than "not production" is deliberate. The
 * negative form also catches `NODE_ENV=test`, which suppresses every message
 * under vitest and makes the sink's own payload untestable — 25 existing
 * assertions went `<suppressed>` when it was written that way. The positive
 * form names the one runtime this is about.
 *
 * The failure direction matters, because this is the single decision for every
 * reason. A false positive would silently gut the sink in production — every
 * row reading `<suppressed>` — which is why the signal is one Next.js
 * guarantees rather than a heuristic. Residual, stated rather than hidden: a
 * production BUILD run locally behind a tunnel still forwards text.
 */
export function isLocalOrigin(hostname: string | undefined): boolean {
  return process.env.NODE_ENV === 'development' || isLocalHostname(hostname);
}

/** Redact known-sensitive substrings. Always run BEFORE truncating. */
export function redactPii(value: string): string {
  return PII_REDACTIONS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    value,
  );
}

/**
 * The canonical redact-then-truncate order, in ONE place.
 *
 * Every payload field derived from arbitrary upstream text must go through
 * this, not through `redactPii` plus an ad-hoc `.slice()`. Truncating first
 * can cut a secret in half and ship the unredacted tail; scanning an unbounded
 * string is the quadratic-regex hazard documented on
 * `MAX_REDACTION_SCAN_LENGTH`. Doing both in the wrong order is easy to get
 * right once and wrong on the next call site, which is why the sequence is not
 * re-implemented per caller (BAL-959 added the second consumer).
 */
export function redactAndTruncate(
  value: string,
  maxLength: number = MESSAGE_PREFIX_LENGTH,
): string {
  return redactPii(value.slice(0, MAX_REDACTION_SCAN_LENGTH)).slice(
    0,
    maxLength,
  );
}

/**
 * A Next.js dynamic-route segment: `[id]`, `[...slug]`, `[[...params]]`.
 * Anchored and restricted to identifier characters so a concrete URL segment
 * that merely contains brackets cannot pose as a template.
 */
const TEMPLATE_SEGMENT = /^\[{1,2}\.{0,3}[a-z0-9_]+\]{1,2}$/i;

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Numeric ids — row ids, years, pagination. */
const NUMERIC_SEGMENT = /^\d{4,}$/;

/**
 * Slug shape: lowercase words joined by hyphens. Covers static route names
 * (`invoices`, `settings`) and public SEO slugs / handles (`natalie-vikner`,
 * `swiss-dance-awards`).
 */
const SLUG_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Credential shape: a long unbroken run mixing letters and digits. Catches
 * hex digests and base64url tokens that would otherwise read as one long
 * "slug" — a lowercase UUID and a 32-char hex session id both satisfy
 * SLUG_SEGMENT, so shape alone is not enough.
 */
const OPAQUE_SEGMENT =
  /^(?=.{12,})(?=[a-z0-9]*\d)(?=[a-z0-9]*[a-z])[a-z0-9]+$/i;

/** True when a path segment may be forwarded verbatim. */
function isSafeSegment(segment: string): boolean {
  if (UUID_SEGMENT.test(segment)) return false;
  if (NUMERIC_SEGMENT.test(segment)) return false;
  if (OPAQUE_SEGMENT.test(segment)) return false;
  return SLUG_SEGMENT.test(segment) && segment.length < 40;
}

/**
 * Route prefixes whose dynamic segment IS a credential. Everything after one
 * of these is redacted regardless of shape.
 *
 * The shape allowlist above already catches today's tokens (a `/m/` token is
 * 16 chars of base64url, so it is mixed-case and opaque). This is the second
 * layer, because shape heuristics are probabilistic and these routes are
 * known-sensitive by construction: a short all-lowercase token would satisfy
 * the slug rule and slip through on shape alone.
 *
 * Keep in sync with the `[token]` / `[code]`-style dynamic routes under
 * `apps/web/app`.
 */
const CREDENTIAL_ROUTE_PREFIXES = [
  '/m/', // magic-link passwordless login
  '/r/', // referral / short code
  '/feedback/', // per-recipient feedback token
  '/auth/invitation/', // admin invitation token
];

/**
 * Reduce a path to a low-cardinality, credential-free route.
 *
 * WHY an allowlist and not a blocklist: the first version of this normalized
 * only UUIDs and long digit runs, and passed everything else through. That
 * leaked `/m/<token>` — a 12-byte base64url passwordless-login credential
 * (`apps/web/app/m/[token]/route.ts`) that is neither a UUID nor numeric — into
 * a third-party analytics store. Any segment that is not recognisably safe is
 * now replaced, so a future route carrying a secret in its path fails closed.
 */
export function normalizeRoutePath(pathname: string): string {
  // A route template (`/m/[token]`) names the parameter instead of its value,
  // so it is already safe and strictly more useful than `:redacted`.
  //
  // The check is STRICT because a concrete request URL can also contain
  // brackets: WHATWG `URL` preserves them in `pathname`, so a loose test let
  // an unauthenticated caller write `/m/[$(whoami)]` straight into the
  // telemetry payload AND skip the credential-prefix rule below.
  // Scoped to the segment the prefix actually governs, not the whole path: a
  // bracket ANYWHERE would otherwise disable the credential rule for every
  // other segment, so `/m/<token>/[x]` would ship the token.
  const credentialPrefix = CREDENTIAL_ROUTE_PREFIXES.find((prefix) => {
    if (!pathname.toLowerCase().startsWith(prefix)) return false;
    const governed = pathname.slice(prefix.length).split('/')[0] ?? '';
    return !TEMPLATE_SEGMENT.test(governed);
  });
  if (credentialPrefix) {
    // Keep the prefix — it identifies WHERE drops happen, which is the whole
    // point of the field — and discard everything after it.
    return `${credentialPrefix}:redacted`;
  }

  const normalized = pathname
    .split('/')
    .map((segment) => {
      if (segment === '') return segment;
      // Next.js route templates (`[token]`, `[...slug]`) carry no value.
      if (TEMPLATE_SEGMENT.test(segment)) return segment;
      return isSafeSegment(segment) ? segment : ':redacted';
    })
    .join('/');

  return normalized.length > MAX_ROUTE_LENGTH
    ? normalized.slice(0, MAX_ROUTE_LENGTH)
    : normalized;
}

/**
 * Derive a safe route from a Sentry event.
 *
 * Prefers the Next.js route TEMPLATE when the event carries one —
 * `instrumentation.ts`'s `onRequestError` tags every server error with
 * `context.routePath`, e.g. `/m/[token]`. A template is inherently safe: it
 * names the parameter instead of its value. Falls back to normalizing the
 * concrete URL.
 */
export function extractRoute(
  url: string | undefined,
  taggedRoute?: unknown,
): string {
  if (typeof taggedRoute === 'string' && taggedRoute.includes('[')) {
    return normalizeRoutePath(taggedRoute);
  }

  if (!url) return 'unknown';

  try {
    // The base makes a bare pathname parseable; the query string is dropped
    // wholesale (auth tokens, emails and search terms all live there).
    const { pathname } = new URL(url, 'http://localhost');
    return normalizeRoutePath(pathname);
  } catch {
    return 'unknown';
  }
}

/**
 * `messagePrefix` when the text is withheld by policy rather than absent.
 *
 * A distinct marker, not an empty string: "we chose not to send this" and "the
 * event carried no message" are different facts, and a reader of the table has
 * no other way to tell them apart.
 */
export const SUPPRESSED_MESSAGE = '<suppressed>';

/**
 * Build the sanitized payload. Single place both halves agree on.
 *
 * `pattern` defaults to empty: only some branches have one — see the field's
 * docblock on `IgnoredDropInfo` for which — and the reasons that match on a
 * flag, a class name or a stack frame would otherwise each have to pass a
 * meaningless `''`.
 * Redaction applies identically to every reason — the silent branches carry
 * arbitrary upstream error text exactly like the pattern branches do.
 *
 * `suppressMessage` withholds the text entirely, for a drop whose ORIGIN makes
 * the text unsafe regardless of shape. Redaction is a shape allowlist: it
 * catches emails, UUIDs, tokens, IBANs and phone numbers, and it cannot catch a
 * personal name, a street address or a date of birth. That residual is
 * acceptable for production error text and is not acceptable for a developer's
 * machine, which may be running against a production data copy
 * (`db:reset:prod`). Set from `isLocalOrigin` in `reportIgnoredDrop`, once per
 * half, so it covers every reason rather than the one branch named `localhost`.
 */
export function buildDropInfo(args: {
  reason: DropReason;
  pattern?: string;
  errorValue: string;
  route: string;
  environment: string;
  suppressMessage?: boolean;
}): IgnoredDropInfo {
  return {
    reason: args.reason,
    pattern: args.pattern ?? '',
    messagePrefix: args.suppressMessage
      ? SUPPRESSED_MESSAGE
      : redactAndTruncate(args.errorValue),
    route: args.route,
    environment: args.environment,
  };
}

/**
 * Best available raw text for an event, for the drop branches that fire before
 * (or instead of) any message matching.
 *
 * Same fallback chain the Cloudflare-fingerprint block already uses, extracted
 * so all ~12 drop sites agree on what "the message" is. Returns raw text: the
 * caller passes it to `buildDropInfo`, which is where redaction happens.
 */
export function resolveEventText(args: {
  message?: unknown;
  originalError?: unknown;
  exceptionValue?: unknown;
}): string {
  const { message, originalError, exceptionValue } = args;
  if (typeof message === 'string' && message !== '') return message;
  if (originalError instanceof Error) return originalError.message;
  if (typeof originalError === 'string') return originalError;
  if (typeof exceptionValue === 'string') return exceptionValue;
  return '';
}
